import { IOrganization } from '@admin-panel/types/lib/org';
import { LOCAL_GATEWAY_AUTH_BYPASS_ENABLED, getAppName } from '@admin-panel/config/env';
import { useOne } from '@refinedev/core';
import { Button, Flex, Spin, Typography } from 'antd';
import { useEffect, useMemo, useState } from 'react';

const LOCAL_DEVELOPER_ORG: IOrganization = {
  id: -1,
  name: 'lyskraft',
  config: {},
};

/** The organization as last read: the app opens on it when the signal is gone (round 14, Imran: a reload offline). */
export const ORG_CACHE_KEY = 'lk.org.lyskraft';

export const readCachedOrg = (): IOrganization | undefined => {
  try {
    const kept = JSON.parse(window.localStorage.getItem(ORG_CACHE_KEY) ?? 'null');
    return kept && typeof kept === 'object' && kept.name ? (kept as IOrganization) : undefined;
  } catch {
    return undefined;
  }
};

const writeCachedOrg = (org: IOrganization) => {
  try {
    window.localStorage.setItem(ORG_CACHE_KEY, JSON.stringify(org));
  } catch {
    // Storage full or blocked: the next offline load cannot open, nothing else changes.
  }
};

/** While the organization cannot be read and none is kept: tried again this often, at most this many times. */
export const ORG_RETRY_MS = 5000;
export const ORG_RETRY_CAP = 60;

/**
 * The organization the app runs under: read from the server, kept in this browser, and the kept one used when the
 * read gets no answer. A failed read is never "not allowed": with nothing kept, `failed` is set and the app says
 * there is no network, tries again by itself (when the phone is back online, and every ORG_RETRY_MS, at most
 * ORG_RETRY_CAP times) and offers Try again.
 */
export const useOrganization = () => {
  const cached = useMemo(readCachedOrg, []);
  const {
    result,
    query: { isLoading, isError, isFetching, refetch },
  } = useOne<IOrganization>({
    resource: 'org',
    id: 'lyskraft',
    queryOptions: { enabled: !LOCAL_GATEWAY_AUTH_BYPASS_ENABLED, retry: 1, retryDelay: 1000 },
    // The shell says it in plain words; never a raw "Network Error (status code: 500)" toast.
    errorNotification: false,
  });
  useEffect(() => {
    if (result) writeCachedOrg(result);
  }, [result]);

  const organization = LOCAL_GATEWAY_AUTH_BYPASS_ENABLED ? LOCAL_DEVELOPER_ORG : (result ?? cached);
  const failed = !organization && isError;

  const [tries, setTries] = useState(0);
  useEffect(() => {
    if (!failed) {
      setTries(0);
      return;
    }
    const again = () => void refetch();
    window.addEventListener('online', again);
    const timer =
      tries < ORG_RETRY_CAP
        ? window.setTimeout(() => {
            setTries(n => n + 1);
            again();
          }, ORG_RETRY_MS)
        : undefined;
    return () => {
      window.removeEventListener('online', again);
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [failed, tries, refetch]);

  return {
    organization,
    loading: !organization && isLoading,
    failed,
    retrying: isFetching,
    retry: () => {
      setTries(0);
      void refetch();
    },
  };
};

/** "No network. Hub will open when the signal is back." (the portal's own name). */
export const noNetworkShellText = (app = getAppName()) =>
  `No network. ${app} will open when the signal is back.`;

/** The app cannot open yet (no organization read, none kept): plain words and one big Try again. */
export const NoNetworkShell: React.FC<{ retrying: boolean; onRetry: () => void; text?: string }> = ({
  retrying,
  onRetry,
  text = noNetworkShellText(),
}) => (
  <Flex
    vertical
    justify="space-between"
    data-testid="shell-no-network"
    style={{ minHeight: '100dvh', padding: 16, boxSizing: 'border-box', background: '#ffffff' }}
  >
    <Typography.Text
      role="alert"
      style={{ display: 'block', marginTop: 48, fontSize: 20, fontWeight: 600, color: '#171717' }}
    >
      {text}
    </Typography.Text>
    <Button
      type="primary"
      size="large"
      block
      loading={retrying}
      onClick={onRetry}
      style={{ minHeight: 56, fontSize: 18, marginBottom: 'env(safe-area-inset-bottom)' }}
    >
      Try again
    </Button>
  </Flex>
);

export const ShellLoading: React.FC = () => (
  <Flex vertical style={{ width: '100%', height: 500 }} justify="space-around" align="center">
    <Spin tip="Loading" spinning size="large" />
  </Flex>
);
